import { Children, forwardRef, useEffect } from "react";

import Flicking from "@egjs/react-flicking";
import "@egjs/react-flicking/dist/flicking.css";

import Box from "@/components/Box";
import { SHOP_BREAKPOINTS } from "@/components/organisations/shop/shopTheme";
import useWindowSize from "@/hooks/use-window-size";
import useBreakPointBasedProp from "@/hooks/useBreakPointBasedProp";
import useCarousel from "@/hooks/useCarousel";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { ActionIcon } from "@/v2/Buttons";

import { dashboardHeaderRowGap } from "./dashboardLayout";

export const DASHBOARD_CAROUSEL_PANEL_GAP = 20;
const panelGap = DASHBOARD_CAROUSEL_PANEL_GAP;
const mobileInset = 20;
const desktopInset = 40;
const shopMobileMaxWidth = Number.parseInt(SHOP_BREAKPOINTS.sm, 10);

export function getDashboardCarouselFlickingStyle(inset) {
  return {
    paddingLeft: inset,
    paddingRight: Math.max(inset - panelGap, 0)
  };
}

function DashboardCarouselNavButtons({ onPrev, onNext }) {
  return (
    <Group gap={10} justifyContent="center">
      <ActionIcon
        bg="dark"
        color="white"
        width={40}
        height={40}
        minWidth={40}
        minHeight={40}
        onClick={onPrev}
        variant="circle"
      >
        <ChevronComponent
          direction="left"
          fill="white"
          style={{
            margin: 0
          }}
        />
      </ActionIcon>
      <ActionIcon
        bg="dark"
        color="white"
        width={40}
        height={40}
        minWidth={40}
        minHeight={40}
        onClick={onNext}
        variant="circle"
      >
        <ChevronComponent
          direction="right"
          fill="white"
          style={{
            margin: 0
          }}
        />
      </ActionIcon>
    </Group>
  );
}

export const DashboardCarouselPanel = forwardRef(function DashboardCarouselPanel(
  { children, maxWidth = 480 },
  ref
) {
  return (
    <Box
      ref={ref}
      className="flicking-panel"
      width="100%"
      minWidth={0}
      maxWidth={maxWidth}
      pr={panelGap}
      display="flex"
      flexDirection="column"
      alignSelf="stretch"
    >
      <Box display="flex" flexDirection="column" flex="1 1 auto" width="100%" minHeight="100%">
        {children}
      </Box>
    </Box>
  );
});

/**
 * Spans its container edge to edge (the container should have no horizontal padding): the first
 * card rests 20px (mobile) / 40px (tablet and up) from the left edge, and cards stay visible right
 * up to the container edges while scrolling. The carousel doesn't loop, as looping would fill the
 * inset with the last card.
 */
export default function DashboardCarousel({ children, title, flickingInset }) {
  const { ref, prev, next } = useCarousel();
  const { width: viewportWidth } = useWindowSize();
  const shopBasedInset =
    !viewportWidth || viewportWidth < shopMobileMaxWidth ? mobileInset : desktopInset;
  const responsiveInset = useBreakPointBasedProp(flickingInset);
  const inset = flickingInset !== undefined ? responsiveInset : shopBasedInset;
  const flickingStyle = getDashboardCarouselFlickingStyle(inset);
  const itemCount = Children.count(children);
  const showNav = itemCount > 1;
  const nav = showNav ? <DashboardCarouselNavButtons onPrev={prev} onNext={next} /> : null;

  useEffect(() => {
    if (!ref.current || itemCount < 1) {
      return;
    }

    ref.current.resize().catch(() => {});
  }, [itemCount, ref, viewportWidth]);

  const flicking = (
    <Box width="100%" minWidth={0} overflow="hidden">
      <Flicking ref={ref} bound align="prev" style={flickingStyle}>
        {children}
      </Flicking>
    </Box>
  );

  if (title) {
    return (
      <>
        <Stack gap={dashboardHeaderRowGap} alignItems="center">
          {title}
          {nav}
        </Stack>
        {flicking}
      </>
    );
  }

  return (
    <>
      {nav}
      {flicking}
    </>
  );
}
